import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "../../components/ui/popover";
import { useToast } from "../../components/ui/use-toast";
import { CalendarIcon, Check, Save, UserPlus, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const employeeFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z.string().min(10, { message: "Please enter a valid phone number." }),
  designation: z.string().min(2, { message: "Designation is required." }),
  department: z.string().min(2, { message: "Department is required." }),
  joiningDate: z.date({ required_error: "Joining date is required." }),
  courses: z.array(z.string()).optional(),
  status: z.enum(["Active", "Suspended", "OffBoarded", "Terminated"]),
  address: z.string().optional(),
  emergencyContact: z.string().optional(),
});

type EmployeeFormValues = z.infer<typeof employeeFormSchema>;

const RegisterEmployee = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const defaultValues: Partial<EmployeeFormValues> = {
    name: "",
    email: "",
    phone: "",
    designation: "",
    department: "",
    joiningDate: new Date(),
    courses: [],
    status: "Active",
    address: "",
    emergencyContact: "",
  };

  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues,
  });

  const availableCourses = [
    "React Fundamentals",
    "Node.js Basics",
    "Advanced JavaScript",
    "Full Stack Development",
    "UI/UX Fundamentals",
    "Design Systems",
    "Agile Project Management",
    "Leadership Skills",
    "Test Automation",
    "QA Methodologies",
    "Database Optimization"
  ];

  const departments = [
    "Engineering",
    "Design",
    "Product",
    "Quality Assurance",
    "Marketing",
    "Human Resources",
    "Administration",
    "Finance"
  ];

  const onSubmit = (data: EmployeeFormValues) => {
    console.log(data);
    
    toast({
      title: "Employee Registered",
      description: `${data.name} has been successfully registered.`,
    });
    
    setTimeout(() => {
      navigate("/employees");
    }, 1500);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text -2xl font-bold">Register New Employee</h1>
        <Button variant="outline" onClick={() => navigate("/employees")}>
          Back to Employees
        </Button>
      </div>

      <Card className="animate-in fade-in duration-500">
        <CardHeader>
          <CardTitle>Employee Registration Form</CardTitle>
          <CardDescription>
            Enter employee details to register them in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter email address" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department*</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {departments.map((dept) => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="designation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Designation*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter designation" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="joiningDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Joining Date*</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date > new Date() || date < new Date("1900-01-01")
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status*</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Suspended">Suspended</SelectItem>
                          <SelectItem value="OffBoarded">Off-Boarded</SelectItem>
                          <SelectItem value="Terminated">Terminated</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter full address" 
                        className="min-h-[100px]" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="emergencyContact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter emergency contact details" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-3">Assigned Courses</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {availableCourses.map((course) => (
                    <div key={course} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={course}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        onChange={(e) => {
                          const currentCourses = form.getValues().courses || [];
                          if (e.target.checked) {
                            form.setValue('courses', [...currentCourses, course]);
                          } else {
                            form.setValue(
                              'courses',
                              currentCourses.filter((c) => c !== course)
                            );
                          }
                        }}
                      />
                      <label htmlFor={course} className="text-sm font-medium">
                        {course}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                >
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
                <Button type="submit">
                  <UserPlus className="mr-2 h-4 w-4" /> Register Employee
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterEmployee;
